import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toolbar } from 'primereact/toolbar';
import { Badge } from 'primereact/badge';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { ProgressBar } from 'primereact/progressbar';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import * as actionTypes from '../../../redux/actionTypes';

/** Max rows per bulk attestation API call */
const BULK_CHUNK_SIZE = 500;

/**
 * Severity mapping for result status tags
 */
const STATUS_SEVERITY = {
  CATEGORIZED: 'info',
  VALIDATED: 'success',
  SUCCESS: 'success',
  PENDING: 'warning',
  REVIEW: 'warning',
  FAILED: 'danger',
  REJECTED: 'danger',
  SKIPPED: 'secondary',
  MODIFIED: 'warning',
};

/**
 * BatchResultsDialog Component
 *
 * Shows processed records for a completed batch in an Excel-like spreadsheet.
 * All original source-data columns from the uploaded file are rendered dynamically.
 * Provides per-row Accept / Deny and global Accept All / Deny All attestation actions.
 * Accepted data is persisted in the attested_data table for future LLM training.
 */
const BatchResultsDialog = ({
  visible,
  onHide,
  batch = null,
  results = [],
  loading = false,
  pagination = null,
  onPageChange = () => {},
}) => {
  const dispatch = useDispatch();
  const acceptingAllBatch = useSelector((state) => state.dataCategory?.acceptingAllBatch ?? false);

  // Selection state for bulk operations
  const [selectedRows, setSelectedRows] = useState([]);

  // Single-row deny dialog
  const [denyDialogVisible, setDenyDialogVisible] = useState(false);
  const [denyTarget, setDenyTarget] = useState(null);
  const [correctedCategory, setCorrectedCategory] = useState('');
  const [denyComments, setDenyComments] = useState('');

  // Bulk deny dialog
  const [bulkDenyDialogVisible, setBulkDenyDialogVisible] = useState(false);
  const [bulkCorrectedCategory, setBulkCorrectedCategory] = useState('');
  const [bulkDenyComments, setBulkDenyComments] = useState('');

  // Bulk attestation progress (for 50K+ rows)
  const [bulkProgress, setBulkProgress] = useState(null); // { sent, total } or null

  // Sorting state
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState(1); // 1 for ASC, -1 for DESC

  // PrimeReact filter state (max 2 filterable columns: suggestedCategory + status)
  const [columnFilters, setColumnFilters] = useState({
    suggestedCategory: { value: null, matchMode: FilterMatchMode.CONTAINS },
    matchedRuleName:   { value: null, matchMode: FilterMatchMode.CONTAINS },
    status:            { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  // ── Helpers ──
  const userId = process.env.REACT_APP_USER_ID || 'SYSTEM';

  const isRowAttested = (row) =>
    row.attestation || row.status === 'VALIDATED';

  /** Rows on the current page that can still be attested */
  const actionableResults = useMemo(
    () => results.filter((r) => !isRowAttested(r)),
    [results]
  );

  /** Count how many selected rows are actionable */
  const actionableSelected = useMemo(
    () => selectedRows.filter((r) => !isRowAttested(r)),
    [selectedRows]
  );

  // ── Summary stats ──
  const stats = useMemo(() => {
    const total = pagination?.totalElements ?? results.length;
    const categorized = pagination?.totalCategorizedElements ?? results.filter((r) => r.status === 'CATEGORIZED').length;
    const validated = pagination?.totalValidatedElements ?? results.filter((r) => r.status === 'VALIDATED').length;
    const failed = pagination?.totalFailedElements ?? results.filter((r) => r.status === 'FAILED').length;
    const pending = Math.max(0, total - categorized - validated - failed);
    return { total, categorized, validated, failed, pending };
  }, [results, pagination]);

  // ── Dynamic columns from originalFields ──
  // Use a ref to keep a stable array reference when the column set hasn't changed,
  // so the useEffect below does NOT re-run on every data reload.
  const prevColumnsRef = useRef([]);
  const originalDataColumns = useMemo(() => {
    if (!results || results.length === 0) return prevColumnsRef.current.length ? prevColumnsRef.current : [];
    const keySet = new Set();
    results.forEach((r) => {
      if (r.originalFields && typeof r.originalFields === 'object') {
        Object.keys(r.originalFields).forEach((k) => keySet.add(k));
      }
    });
    const newColumns = Array.from(keySet);
    const prev = prevColumnsRef.current;
    // Return the previous reference if the column names are identical — prevents
    // unnecessary useEffect triggers and PrimeReact filter-state desyncs.
    if (newColumns.length === prev.length && newColumns.every((c, i) => c === prev[i])) {
      return prev;
    }
    prevColumnsRef.current = newColumns;
    return newColumns;
  }, [results]);

  // Seed columnFilters with entries for each NEW dynamic originalField column.
  // Only runs when the column set genuinely changes, thanks to the stable reference above.
  useEffect(() => {
    if (originalDataColumns.length === 0) return;
    setColumnFilters((prev) => {
      const newKeys = originalDataColumns.filter(
        (colName) => !prev[`originalFields.${colName}`]
      );
      if (newKeys.length === 0) return prev; // nothing to add — return same reference
      const next = { ...prev };
      newKeys.forEach((colName) => {
        next[`originalFields.${colName}`] = { value: null, matchMode: FilterMatchMode.CONTAINS };
      });
      return next;
    });
  }, [originalDataColumns]);

  // ── Dialog header ──
  const title = batch
    ? `Results — ${batch.fileName || batch.batchId} (${pagination?.totalElements ?? results.length} records)`
    : 'Batch Results';

  // ════════════════════════════════════════════
  //  Column body templates
  // ════════════════════════════════════════════

  const statusTemplate = (rowData) => {
    const status = rowData.status || '';
    const displayLabel = status === 'FAILED' ? 'Uncategorized' : (status || '—');
    return (
      <Tag
        value={displayLabel}
        severity={STATUS_SEVERITY[status?.toUpperCase()] || 'info'}
      />
    );
  };

  const confidenceTemplate = (rowData) => {
    const score = rowData.confidence ?? rowData.confidenceScore;
    if (score == null) return <span style={{ color: '#999' }}>—</span>;
    const pct = score > 1 ? Math.round(score) : Math.round(score * 100);
    let severity = 'danger';
    if (pct >= 80) {
      severity = 'success';
    } else if (pct >= 60) {
      severity = 'warning';
    }
    return <Tag value={`${pct}%`} severity={severity} />;
  };

  const originalFieldBody = (rowData, colName) => {
    const val = rowData.originalFields?.[colName];
    if (!val) return <span style={{ color: '#999' }}>—</span>;
    if (val.length > 80) return <span title={val}>{val.substring(0, 80)}…</span>;
    return val;
  };

  const suggestedCategoryTemplate = (rowData) => (
    <span style={{ color: rowData.suggestedCategory ? '#2563eb' : '#999', fontWeight: 600 }}>
      {rowData.suggestedCategory || '—'}
    </span>
  );

  const matchedRuleTemplate = (rowData) => rowData.matchedRuleName || '—';

  const errorTemplate = (rowData) => {
    const err = rowData.rejectionReason || rowData.errorMessage;
    if (!err) return <span style={{ color: '#999' }}>—</span>;
    return (
      <span title={err} style={{ color: '#ef4444' }}>
        {err.length > 50 ? `${err.substring(0, 50)}…` : err}
      </span>
    );
  };

  const statusFilterElement = () => (
    <Dropdown
      value={columnFilters['status']?.value ?? null}
      options={[
        { label: 'All', value: null },
        { label: 'Categorized', value: 'CATEGORIZED' },
        { label: 'Validated', value: 'VALIDATED' },
        { label: 'Uncategorized', value: 'FAILED' },
        { label: 'Pending', value: 'PENDING' },
      ]}
      onChange={(e) => {
        const newFilters = (prev) => ({
          ...prev,
          status: { ...prev.status, value: e.value },
        });
        setColumnFilters((prev) => {
          const updated = newFilters(prev);
          // Immediately trigger API call with the updated status value
          if (typeof onPageChange === 'function') {
            onPageChange({
              page: 0,
              rows: pagination?.size ?? 50,
              first: 0,
              sortField,
              sortOrder,
              filters: toApiFilters(updated),
            });
          }
          return updated;
        });
      }}
      placeholder="Status…"
      style={{ minWidth: '120px', fontSize: '0.8rem' }}
    />
  );

  // ════════════════════════════════════════════
  //  Per-row Accept / Deny
  // ════════════════════════════════════════════

  const handleAccept = (rowData) => {
    dispatch({
      type: actionTypes.ATTEST_RESULT,
      payload: {
        resultId: rowData.resultId || rowData.id,
        verdict: 'ACCEPTED',
        attestedBy: userId,
      },
    });
  };

  const handleDenyClick = (rowData) => {
    setDenyTarget(rowData);
    setCorrectedCategory('');
    setDenyComments('');
    setDenyDialogVisible(true);
  };

  const handleDenySubmit = () => {
    if (!correctedCategory.trim()) return;
    dispatch({
      type: actionTypes.ATTEST_RESULT,
      payload: {
        resultId: denyTarget.resultId || denyTarget.id,
        verdict: 'DENIED',
        correctedCategory: correctedCategory.trim(),
        attestedBy: userId,
        comments: denyComments.trim() || undefined,
      },
    });
    setDenyDialogVisible(false);
    setDenyTarget(null);
  };

  const actionsTemplate = (rowData) => {
    const attested = isRowAttested(rowData);
    return (
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
        <Button
          icon="pi pi-check"
          className="p-button-rounded p-button-text p-button-success"
          onClick={() => handleAccept(rowData)}
          disabled={attested}
          tooltip="Accept suggestion"
          tooltipOptions={{ position: 'top' }}
          style={{ width: '2rem', height: '2rem' }}
        />
        <Button
          icon="pi pi-times"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleDenyClick(rowData)}
          disabled={attested}
          tooltip="Deny & provide correct category"
          tooltipOptions={{ position: 'top' }}
          style={{ width: '2rem', height: '2rem' }}
        />
      </div>
    );
  };

  // ════════════════════════════════════════════
  //  Global Accept All / Deny All
  // ════════════════════════════════════════════

  /**
   * Dispatch attestation requests in chunks of BULK_CHUNK_SIZE.
   * For small sets (<= BULK_CHUNK_SIZE) dispatches immediately.
   * For large sets, dispatches sequentially with progress tracking.
   */
  const dispatchChunked = (requests) => {
    if (requests.length <= BULK_CHUNK_SIZE) {
      dispatch({ type: actionTypes.BULK_ATTEST_RESULTS, payload: requests });
      return;
    }
    // Chunk and dispatch sequentially
    const chunks = [];
    for (let i = 0; i < requests.length; i += BULK_CHUNK_SIZE) {
      chunks.push(requests.slice(i, i + BULK_CHUNK_SIZE));
    }
    setBulkProgress({ sent: 0, total: requests.length });
    let sent = 0;
    chunks.forEach((chunk, idx) => {
      setTimeout(() => {
        dispatch({ type: actionTypes.BULK_ATTEST_RESULTS, payload: chunk });
        sent += chunk.length;
        setBulkProgress({ sent, total: requests.length });
        if (sent >= requests.length) {
          // Clear progress after a short delay
          setTimeout(() => setBulkProgress(null), 2000);
        }
      }, idx * 300); // stagger chunks by 300ms
    });
  };

  // ════════════════════════════════════════════
  //  Sorting & Filtering
  // ════════════════════════════════════════════

  /** Handle page change — inject current sort/filter state so they survive pagination */
  const handlePageChange = (e) => {
    if (typeof onPageChange === 'function') {
      onPageChange({
        page: e.page,
        rows: e.rows,
        first: e.first,
        sortField,
        sortOrder,
        filters: toApiFilters(columnFilters),
      });
    }
  };

  /** Handle sort event from DataTable */
  const handleSort = (e) => {
    const newSortField = e.sortField || '';
    const newSortOrder = e.sortOrder !== undefined ? e.sortOrder : 1;
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    if (typeof onPageChange === 'function') {
      onPageChange({
        page: 0,
        rows: pagination?.size ?? 50,
        first: 0,
        sortField: newSortField,
        sortOrder: newSortOrder,
        filters: toApiFilters(columnFilters),
      });
    }
  };

  /** Handle filter event from DataTable — only updates local state, does NOT call API.
   * Merge instead of replace: PrimeReact's e.filters may not include dynamically-seeded
   * originalFields.* keys, so we must preserve whatever is already in our state. */
  const handleFilter = (e) => {
    setColumnFilters((prev) => ({ ...prev, ...e.filters }));
  };

  /**
   * Map UI status values to backend-compatible values.
   * The UI uses transformed statuses (CATEGORIZED, VALIDATED, FAILED, PENDING)
   * but the backend stores raw statuses (SUCCESS, FAILED) and a separate isValidated flag.
   */
  const mapStatusToBackend = (uiStatus) => {
    switch (uiStatus) {
      case 'CATEGORIZED': return 'CATEGORIZED';
      case 'VALIDATED': return 'VALIDATED';
      case 'FAILED': return 'FAILED';
      case 'PENDING': return 'PENDING';
      default: return uiStatus;
    }
  };

  /** Derive API-compatible filter array from current columnFilters state */
  const toApiFilters = (filters) =>
    Object.entries(filters)
      .filter(([, f]) => f.value !== null && f.value !== '')
      .slice(0, 2)
      .map(([field, f]) => ({
        field,
        value: field === 'status' ? mapStatusToBackend(f.value) : f.value
      }));

  /** Shared text filter element.
   * Directly updates columnFilters state — does NOT go through options.filterCallback
   * to avoid PrimeReact's built-in filterDelay (300ms) which would cause stale state
   * when the user clicks Apply Filters quickly after typing.
   */
  const textFilterElement = (options) => (
    <InputText
      value={columnFilters[options.field]?.value ?? ''}
      onChange={(e) => {
        const val = e.target.value;
        setColumnFilters((prev) => ({
          ...prev,
          [options.field]: { ...prev[options.field], value: val === '' ? null : val },
        }));
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          // Resolve the very latest filter state via callback to avoid stale closure
          setColumnFilters((currentFilters) => {
            if (typeof onPageChange === 'function') {
              onPageChange({
                page: 0,
                rows: pagination?.size ?? 50,
                first: 0,
                sortField,
                sortOrder,
                filters: toApiFilters(currentFilters),
              });
            }
            return currentFilters; // no state change — just reading latest value
          });
        }
      }}
      placeholder={options.placeholder || 'Filter…'}
      style={{ width: '100%', fontSize: '0.8rem' }}
    />
  );

  /** Apply current filters — triggers API call */
  const applyFilters = () => {
    if (typeof onPageChange === 'function') {
      onPageChange({
        page: 0,
        rows: pagination?.size ?? 50,
        first: 0,
        sortField,
        sortOrder,
        filters: toApiFilters(columnFilters),
      });
    }
  };

  /** Clear all filters and reload */
  const clearFilters = () => {
    const cleared = Object.fromEntries(
      Object.keys(columnFilters).map((key) => [
        key,
        { value: null, matchMode: columnFilters[key].matchMode },
      ])
    );
    setColumnFilters(cleared);
    if (typeof onPageChange === 'function') {
      onPageChange({
        page: 0,
        rows: pagination?.size ?? 50,
        first: 0,
        sortField,
        sortOrder,
        filters: [],
      });
    }
  };

  const hasActiveFilters = Object.values(columnFilters).some(
    (f) => f.value !== null && f.value !== ''
  );

  /** Accept All Selected (or all actionable if none selected) */
  const handleAcceptAll = () => {
    const targets = actionableSelected.length > 0 ? actionableSelected : actionableResults;
    if (targets.length === 0) return;

    confirmDialog({
      message: `Accept the engine's suggested category for ${targets.length} result(s)? Accepted data will be stored for future LLM training.`,
      header: 'Accept All Confirmation',
      icon: 'pi pi-check-circle',
      acceptClassName: 'p-button-success',
      accept: () => {
        const requests = targets.map((r) => ({
          resultId: r.resultId || r.id,
          verdict: 'ACCEPTED',
          attestedBy: userId,
        }));
        dispatchChunked(requests);
        setSelectedRows([]);
      },
    });
  };

  /** Accept only the rows that the engine successfully categorized — server-side, covers ALL pages */
  const handleAcceptCategorized = () => {
    const totalCategorized = pagination?.totalCategorizedElements ?? stats.categorized;
    if (totalCategorized === 0) return;

    confirmDialog({
      message: `Accept the engine's suggested category for all ${totalCategorized.toLocaleString()} successfully categorized result(s) across every page? This will mark them as validated on the server and will be used for future LLM training.`,
      header: 'Accept All Categorized — Whole Batch',
      icon: 'pi pi-check-circle',
      acceptClassName: 'p-button-success',
      accept: () => {
        dispatch({
          type: actionTypes.ACCEPT_ALL_BATCH,
          payload: { batchId: batch?.batchId },
        });
        setSelectedRows([]);
      },
    });
  };

  /** Deny All Selected — opens bulk deny dialog for corrected category */
  const handleDenyAll = () => {
    const targets = actionableSelected.length > 0 ? actionableSelected : actionableResults;
    if (targets.length === 0) return;
    setBulkCorrectedCategory('');
    setBulkDenyComments('');
    setBulkDenyDialogVisible(true);
  };

  const handleBulkDenySubmit = () => {
    if (!bulkCorrectedCategory.trim()) return;
    const targets = actionableSelected.length > 0 ? actionableSelected : actionableResults;
    const requests = targets.map((r) => ({
      resultId: r.resultId || r.id,
      verdict: 'DENIED',
      correctedCategory: bulkCorrectedCategory.trim(),
      attestedBy: userId,
      comments: bulkDenyComments.trim() || undefined,
    }));
    dispatchChunked(requests);
    setBulkDenyDialogVisible(false);
    setSelectedRows([]);
  };

  // Also update handleSort to use toApiFilters helper
  // (no functional change, just consistency)

  // ════════════════════════════════════════════
  //  Toolbar above the grid
  // ════════════════════════════════════════════

  const toolbarLeftContent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
      <Button
        icon="pi pi-filter"
        size="small"
        severity="secondary"
        onClick={applyFilters}
        tooltip="Apply filters"
        tooltipOptions={{ position: 'top' }}
        aria-label="Apply filters"
      />
      {hasActiveFilters && (
        <Button
          icon="pi pi-filter-slash"
          size="small"
          severity="secondary"
          outlined
          onClick={clearFilters}
          tooltip="Clear filters"
          tooltipOptions={{ position: 'top' }}
          aria-label="Clear filters"
        />
      )}
      <Button
        label="Accept All"
        icon="pi pi-check-circle"
        severity="success"
        size="small"
        onClick={handleAcceptAll}
        disabled={loading || actionableResults.length === 0}
        badge={String(actionableSelected.length > 0 ? actionableSelected.length : actionableResults.length)}
        badgeClassName="p-badge-contrast"
        tooltip={actionableSelected.length > 0
          ? `Accept ${actionableSelected.length} selected row(s)`
          : `Accept all ${actionableResults.length} pending row(s)`}
        tooltipOptions={{ position: 'top' }}
      />
      <Button
        label="Accept Categorized"
        icon="pi pi-check"
        severity="info"
        size="small"
        onClick={handleAcceptCategorized}
        disabled={loading || acceptingAllBatch || (pagination?.totalCategorizedElements ?? stats.categorized) === 0}
        badge={String(pagination?.totalCategorizedElements ?? stats.categorized)}
        badgeClassName="p-badge-contrast"
        tooltip={`Accept all ${(pagination?.totalCategorizedElements ?? stats.categorized).toLocaleString()} row(s) that the engine successfully categorized (all pages)`}
        tooltipOptions={{ position: 'top' }}
      />
      <Button
        label="Deny All"
        icon="pi pi-times-circle"
        severity="danger"
        size="small"
        onClick={handleDenyAll}
        disabled={loading || actionableResults.length === 0}
        badge={String(actionableSelected.length > 0 ? actionableSelected.length : actionableResults.length)}
        badgeClassName="p-badge-contrast"
        tooltip={actionableSelected.length > 0
          ? `Deny ${actionableSelected.length} selected row(s)`
          : `Deny all ${actionableResults.length} pending row(s)`}
        tooltipOptions={{ position: 'top' }}
      />
      {actionableSelected.length > 0 && (
        <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
          {actionableSelected.length} row(s) selected
        </span>
      )}
    </div>
  );

  const toolbarRightContent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.85rem' }}>
      <span>
        <Tag value={`${stats.categorized} Categorized`} severity="info" style={{ marginRight: 4 }} />
        <Tag value={`${stats.validated} Accepted`} severity="success" style={{ marginRight: 4 }} />
        <Tag value={`${stats.failed} Uncategorized`} severity="danger" style={{ marginRight: 4 }} />
        {stats.pending > 0 && <Tag value={`${stats.pending} Other`} severity="warning" />}
      </span>
    </div>
  );

  // ════════════════════════════════════════════
  //  Footer
  // ════════════════════════════════════════════

  const dialogFooter = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
        <i className="pi pi-info-circle" style={{ marginRight: 4 }} />
        Accepted records are saved as attested training data for future LLM model upgrades.
      </span>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Button
          label={acceptingAllBatch
            ? 'Accepting…'
            : `Accept All Categorized (${(pagination?.totalCategorizedElements ?? stats.categorized).toLocaleString()})`}
          icon={acceptingAllBatch ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
          className="p-button-info"
          onClick={handleAcceptCategorized}
          disabled={acceptingAllBatch || (pagination?.totalCategorizedElements ?? stats.categorized) === 0}
          tooltip="Accept all successfully categorized results across ALL pages on the server"
          tooltipOptions={{ position: 'top' }}
        />
        <Button label="Close" icon="pi pi-times" onClick={onHide} className="p-button-text" />
      </div>
    </div>
  );

  // ── Deny sub-dialog footer ──
  const denyDialogFooter = (
    <div>
      <Button label="Cancel" icon="pi pi-times" className="p-button-text"
        onClick={() => setDenyDialogVisible(false)} />
      <Button label="Submit Denial" icon="pi pi-check" severity="danger"
        onClick={handleDenySubmit} disabled={!correctedCategory.trim()} />
    </div>
  );

  const bulkDenyDialogFooter = (
    <div>
      <Button label="Cancel" icon="pi pi-times" className="p-button-text"
        onClick={() => setBulkDenyDialogVisible(false)} />
      <Button
        label={`Deny ${actionableSelected.length > 0 ? actionableSelected.length : actionableResults.length} Result(s)`}
        icon="pi pi-check"
        severity="danger"
        onClick={handleBulkDenySubmit}
        disabled={!bulkCorrectedCategory.trim()}
      />
    </div>
  );

  return (
    <>
      <ConfirmDialog />

      <Dialog
        header={title}
        visible={visible}
        onHide={onHide}
        style={{ width: '95vw', maxWidth: '1600px' }}
        footer={dialogFooter}
        maximizable
        draggable={false}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          </div>
        ) : (
          <>
            {/* ── Global Accept / Deny Toolbar ── */}
            <Toolbar
              start={toolbarLeftContent}
              end={toolbarRightContent}
              style={{ marginBottom: '0.5rem', padding: '0.5rem 1rem', borderRadius: '8px' }}
            />

            {/* ── Bulk attestation progress bar ── */}
            {bulkProgress && (
              <div style={{ marginBottom: '0.5rem' }}>
                <ProgressBar
                  value={Math.round((bulkProgress.sent / bulkProgress.total) * 100)}
                  displayValueTemplate={() =>
                    `Sending attestations: ${bulkProgress.sent.toLocaleString()} / ${bulkProgress.total.toLocaleString()}`
                  }
                  style={{ height: '1.5rem' }}
                />
              </div>
            )}

            <DataTable
              value={results}
              lazy={!!pagination}
              paginator
              rows={pagination?.size ?? 50}
              rowsPerPageOptions={[25, 50, 100]}
              totalRecords={pagination?.totalElements ?? results.length}
              first={(pagination?.page ?? 0) * (pagination?.size ?? 50)}
              sortField={sortField}
              sortOrder={sortOrder}
              sortMode="single"
              filters={columnFilters}
              filterDisplay="row"
              onPage={handlePageChange}
              onSort={handleSort}
              onFilter={handleFilter}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
              emptyMessage="No records found for this batch."
              className="batch-results-table"
              stripedRows
              scrollable
              scrollHeight="55vh"
              showGridlines
              size="small"
              style={{ fontSize: '0.85rem' }}
              dataKey="id"
              selection={selectedRows}
              onSelectionChange={(e) => setSelectedRows(e.value)}
            >
              {/* ── Checkbox selection column ── */}
              <Column
                selectionMode="multiple"
                headerStyle={{ width: '3em' }}
                frozen
                exportable={false}
              />

              {/* ── Row number ── */}
              <Column
                field="rowNumber"
                header="#"
                sortable
                frozen
                style={{ minWidth: '50px', maxWidth: '60px', textAlign: 'center' }}
              />

              {/* ── Dynamic original data columns (Excel-like) ── */}
              {originalDataColumns.map((colName) => (
                <Column
                  key={colName}
                  field={`originalFields.${colName}`}
                  header={colName}
                  sortable
                  filter
                  filterField={`originalFields.${colName}`}
                  filterMatchMode="contains"
                  filterPlaceholder={`Filter ${colName}…`}
                  filterElement={textFilterElement}
                  showFilterMenu={false}
                  style={{ minWidth: '140px', maxWidth: '300px' }}
                  body={(rowData) => originalFieldBody(rowData, colName)}
                />
              ))}

              {/* ── Categorization result columns ── */}
              <Column
                field="suggestedCategory"
                header="Suggested Category"
                sortable
                filter
                filterField="suggestedCategory"
                filterMatchMode="contains"
                filterPlaceholder="Filter category…"
                filterElement={textFilterElement}
                showFilterMenu={false}
                style={{ minWidth: '160px' }}
                body={suggestedCategoryTemplate}
              />

              <Column
                field="matchedRuleName"
                header="Matched Rule"
                sortable
                filter
                filterField="matchedRuleName"
                filterMatchMode="contains"
                filterPlaceholder="Filter rule…"
                filterElement={textFilterElement}
                showFilterMenu={false}
                style={{ minWidth: '140px' }}
                body={matchedRuleTemplate}
              />

              <Column
                field="confidence"
                header="Confidence"
                body={confidenceTemplate}
                sortable
                sortField="confidence"
                style={{ minWidth: '100px', textAlign: 'center' }}
              />

              <Column
                field="status"
                header="Status"
                body={statusTemplate}
                sortable
                filter
                filterField="status"
                filterMatchMode="equals"
                showFilterMenu={false}
                filterElement={statusFilterElement}
                style={{ minWidth: '140px', textAlign: 'center' }}
              />

              <Column
                header="Error"
                body={errorTemplate}
                style={{ minWidth: '180px' }}
              />

              {/* ── Per-row Accept / Deny buttons ── */}
              <Column
                body={actionsTemplate}
                header="Accept / Deny"
                exportable={false}
                frozen
                alignFrozen="right"
                style={{ minWidth: '110px', textAlign: 'center' }}
              />
            </DataTable>
          </>
        )}
      </Dialog>

      {/* ── Single-row Deny dialog ── */}
      <Dialog
        header="Deny Categorization"
        visible={denyDialogVisible}
        onHide={() => setDenyDialogVisible(false)}
        style={{ width: '500px', maxWidth: '95vw' }}
        footer={denyDialogFooter}
        modal
      >
        {denyTarget && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <strong>Row #{denyTarget.rowNumber}</strong>
              {denyTarget.suggestedCategory && (
                <span>
                  {' '}— engine suggested:{' '}
                  <Tag value={denyTarget.suggestedCategory} severity="info" />
                </span>
              )}
            </div>
            <div className="p-field">
              <label htmlFor="deny-corrected-category"><strong>Correct Category *</strong></label>
              <InputText
                id="deny-corrected-category"
                value={correctedCategory}
                onChange={(e) => setCorrectedCategory(e.target.value)}
                placeholder="Enter the correct category"
                style={{ width: '100%', marginTop: '0.25rem' }}
                autoFocus
              />
            </div>
            <div className="p-field">
              <label htmlFor="deny-comments">Comments (optional)</label>
              <InputTextarea
                id="deny-comments"
                value={denyComments}
                onChange={(e) => setDenyComments(e.target.value)}
                rows={3}
                placeholder="Why is this categorization incorrect?"
                style={{ width: '100%', marginTop: '0.25rem' }}
              />
            </div>
          </div>
        )}
      </Dialog>

      {/* ── Bulk Deny dialog ── */}
      <Dialog
        header="Deny All — Provide Correct Category"
        visible={bulkDenyDialogVisible}
        onHide={() => setBulkDenyDialogVisible(false)}
        style={{ width: '520px', maxWidth: '95vw' }}
        footer={bulkDenyDialogFooter}
        modal
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ margin: 0, color: '#6b7280' }}>
            This will deny{' '}
            <strong>{actionableSelected.length > 0 ? actionableSelected.length : actionableResults.length}</strong>{' '}
            result(s) and assign the corrected category below to all of them.
            Denied records with corrections are also saved for LLM training data.
          </p>
          <div className="p-field">
            <label htmlFor="bulk-deny-category"><strong>Correct Category *</strong></label>
            <InputText
              id="bulk-deny-category"
              value={bulkCorrectedCategory}
              onChange={(e) => setBulkCorrectedCategory(e.target.value)}
              placeholder="Enter the correct category for all selected rows"
              style={{ width: '100%', marginTop: '0.25rem' }}
              autoFocus
            />
          </div>
          <div className="p-field">
            <label htmlFor="bulk-deny-comments">Comments (optional)</label>
            <InputTextarea
              id="bulk-deny-comments"
              value={bulkDenyComments}
              onChange={(e) => setBulkDenyComments(e.target.value)}
              rows={3}
              placeholder="Why are these categorizations incorrect?"
              style={{ width: '100%', marginTop: '0.25rem' }}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

BatchResultsDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  batch: PropTypes.object,
  results: PropTypes.array,
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    size: PropTypes.number,
    totalElements: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  onPageChange: PropTypes.func,
};

export default BatchResultsDialog;

